/**
 * CSV primitives shared by the column-mapping importers.
 *
 * Lifted out of `SourceEntityColumnManagerModal`, which had the only implementation. The RDV
 * column-mappings modal needs the same parsing, and two hand-rolled CSV parsers that drift apart
 * is exactly the kind of thing that produces "it worked in the other screen" bug reports.
 *
 * Deliberately small: no dependency, no streaming, no dialect detection. The files these read are
 * produced by the app's own Export CSV or hand-made by a configurator, and are a few hundred rows
 * at most.
 */

/** Quote a value for CSV output, doubling any embedded quotes. */
export const csvEscape = (value: string | boolean | number | null | undefined): string => {
  const raw = String(value ?? "");
  return /[",\n]/.test(raw) ? `"${raw.replace(/"/g, '""')}"` : raw;
};

/** Delimiters worth guessing between. Comma first, so it wins a tie. */
const CANDIDATE_DELIMITERS = [",", ";", "\t"] as const;

/**
 * Strip a UTF-8 byte-order mark.
 *
 * Excel writes one on every CSV it saves. Left in place it becomes part of the first header cell,
 * so `source_entity_column_name` stops matching and the header is read as data.
 */
export const stripBom = (text: string): string =>
  text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

/** Count a character in a line, ignoring anything inside quotes. */
const countOutsideQuotes = (line: string, target: string): number => {
  let count = 0;
  let inQuotes = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"' && inQuotes && line[index + 1] === '"') {
      index += 1;
      continue;
    }
    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes && char === target) {
      count += 1;
    }
  }
  return count;
};

/**
 * Guess which delimiter a line uses.
 *
 * Excel writes the LOCALE's list separator, not a comma — on a Swedish or German machine that is a
 * semicolon, and running Text to Columns and saving is enough to produce one. A semicolon file read
 * as comma-separated collapses each line into a single cell, which silently imports nothing: the
 * source is the whole line and the target is empty, and an empty target means "leave it alone".
 * Guessing costs nothing and removes a whole class of "it just didn't work".
 */
export const detectDelimiter = (line: string): string => {
  let best = ",";
  let bestCount = 0;
  CANDIDATE_DELIMITERS.forEach((candidate) => {
    const count = countOutsideQuotes(line, candidate);
    if (count > bestCount) {
      best = candidate;
      bestCount = count;
    }
  });
  return best;
};

/** Split one CSV line into cells, honouring quoted fields and doubled quotes inside them. */
export const parseCsvLine = (line: string, delimiter: string = ","): string[] => {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];

    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      index += 1;
      continue;
    }

    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }

    if (char === delimiter && !inQuotes) {
      result.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  result.push(current);
  return result;
};

/**
 * Split a whole CSV document into non-empty lines, then cells.
 *
 * Blank lines are dropped — trailing newlines are normal in exported files and a row of empty
 * cells is never meaningful here. Handles CRLF, because these files come off Windows machines.
 */
export const parseCsvRows = (text: string): string[][] => {
  const lines = stripBom(text)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length === 0) {
    return [];
  }
  // Guessed from the first line — the header, in every file these read — and then used for all of
  // them, so one odd row cannot change how the rest are split.
  const delimiter = detectDelimiter(lines[0]);
  return lines.map((line) => parseCsvLine(line, delimiter));
};

/**
 * Read a header row into a lookup, so callers can accept any column order.
 *
 * Returns `null` when the row does not look like a header — that is, when none of `expected`
 * appears in it — which lets a caller fall back to fixed positions for a file with no header.
 */
export const readCsvHeader = (
  cells: string[],
  expected: string[]
): Map<string, number> | null => {
  const normalized = cells.map((cell) => cell.trim().toLowerCase());
  if (!expected.some((name) => normalized.includes(name))) {
    return null;
  }
  const index = new Map<string, number>();
  normalized.forEach((name, position) => {
    if (name && !index.has(name)) {
      index.set(name, position);
    }
  });
  return index;
};
